import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import Modal from "../../../components/base/Modal";
import {
  Badge,
  Button,
  Input,
  Textarea,
  confirmDialog,
} from "../../../components/ui";
import { useCentinelaCatalogs } from "../../../features/centinela/queries";
import { useCallCentinelaUnit } from "../../../features/centinela/mutations";
import type { CentinelaUnitCallResponse } from "../../../features/centinela/types";
import type { Unit } from "../../../store/admin/unitsStore";
import { centinelaErrorKey } from "../services/centinelaErrors";
import { CENTINELA_CALL_TYPE_ICONS } from "../theme";

const FALLBACK_CALL_ICON = "ri-phone-line";

export interface UnitCallModalProps {
  unit: Unit;
  deviceId: string | null;
  operatorName: string;
  hasOperatorPhone: boolean;
  onClose: () => void;
}

export default function UnitCallModal({
  unit,
  deviceId,
  operatorName,
  hasOperatorPhone,
  onClose,
}: UnitCallModalProps) {
  const { t } = useTranslation("common");

  const catalogsQuery = useCentinelaCatalogs();
  const callUnit = useCallCentinelaUnit();

  const [selected, setSelected] = useState<string | null>(null);
  const [fact, setFact] = useState("");
  const [reason, setReason] = useState("");
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [placed, setPlaced] = useState<CentinelaUnitCallResponse | null>(null);

  const callTypes = useMemo(
    () =>
      (catalogsQuery.data?.call_types ?? []).filter(
        (type) => type.profile === "operador",
      ),
    [catalogsQuery.data],
  );

  const selectedType = callTypes.find((type) => type.id === selected) ?? null;

  const blocked = !deviceId || !hasOperatorPhone;

  const placeCall = async () => {
    if (!selected || !deviceId) return;

    const confirmed = await confirmDialog({
      title: t("monitoreoIa.units.callModal.confirmTitle"),
      text: t("monitoreoIa.units.callModal.confirmText", {
        operator: operatorName,
        unit: unit.name,
      }),
      confirmText: t("monitoreoIa.units.callModal.confirmYes"),
      cancelText: t("common.cancel"),
      tone: "warning",
      icon: "warning",
    });

    if (!confirmed) return;

    setErrorKey(null);

    callUnit
      .mutateAsync({
        deviceId,
        input: {
          tipo: selected,
          ...(fact.trim() ? { hecho: fact.trim() } : {}),
          ...(reason.trim() ? { motivo: reason.trim() } : {}),
        },
      })
      .then((data) => setPlaced(data ?? {}))
      .catch((error) =>
        setErrorKey(
          centinelaErrorKey(error, "monitoreoIa.units.callModal.errorGeneric"),
        ),
      );
  };

  return (
    <Modal
      title={t("monitoreoIa.units.callModal.title")}
      onClose={onClose}
      width="w-full max-w-xl"
      content={
        placed ? (
          <div className="rounded-xl border border-success/30 bg-success/10 p-5 text-center">
            <span className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-success/15">
              <i className="ri-phone-line text-lg text-success" />
            </span>

            <p className="text-[13.5px] font-bold text-content">
              {t("monitoreoIa.units.callModal.dialing")}
            </p>

            <p className="mt-1 text-xs leading-relaxed text-content-secondary">
              {t("monitoreoIa.units.callModal.dialingHint", {
                operator: operatorName,
                unit: unit.name,
              })}
            </p>

            {selectedType ? (
              <span className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-line bg-card px-3 py-1.5 text-[11px] font-medium text-content-secondary">
                <i
                  className={`${CENTINELA_CALL_TYPE_ICONS[selectedType.id] ?? FALLBACK_CALL_ICON} text-xs`}
                />
                {selectedType.label}
              </span>
            ) : null}

            <Button
              variant="secondary"
              size="sm"
              fullWidth
              className="mt-4"
              onClick={onClose}
            >
              {t("monitoreoIa.testCall.close")}
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center gap-3 rounded-xl border border-line bg-subtle px-3 py-2.5">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary-500/10">
                <i className="ri-phone-line text-primary-500" />
              </span>
              <p className="min-w-0 flex-1 truncate text-xs text-content-secondary">
                {operatorName} · {unit.name}
              </p>
              <Badge tone="warning" size="sm">
                {t("monitoreoIa.units.callModal.realCall")}
              </Badge>
            </div>

            <p className="flex items-start gap-2 rounded-xl border border-warning/30 bg-warning/10 px-3 py-2 text-[11.5px] leading-relaxed text-warning">
              <i className="ri-money-dollar-circle-line mt-0.5 shrink-0 text-[13px]" />
              {t("monitoreoIa.units.callModal.realCallWarning")}
            </p>

            {blocked ? (
              <p className="rounded-xl border border-error/30 bg-error/10 px-3 py-2 text-xs text-error">
                {t("monitoreoIa.units.callModal.noPhoneBlocker")}
              </p>
            ) : null}

            <p className="text-[12.5px] text-content-secondary">
              {t("monitoreoIa.units.callModal.pick")}
            </p>

            <div className="max-h-[30dvh] space-y-2 overflow-y-auto pr-1">
              {callTypes.map((type) => (
                <button
                  key={type.id}
                  type="button"
                  disabled={blocked}
                  onClick={() => setSelected(type.id)}
                  className={`flex w-full items-start gap-3 rounded-xl border p-3 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
                    selected === type.id
                      ? "border-primary-500/40 bg-primary-500/10"
                      : "border-line bg-card hover:bg-subtle"
                  }`}
                >
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-line bg-subtle text-content-secondary">
                    <i
                      className={`${CENTINELA_CALL_TYPE_ICONS[type.id] ?? FALLBACK_CALL_ICON} text-sm`}
                    />
                  </span>

                  <span className="min-w-0 flex-1">
                    <span
                      className={`block text-[12.5px] font-semibold leading-tight ${
                        selected === type.id
                          ? "text-primary-500"
                          : "text-content"
                      }`}
                    >
                      {type.label}
                    </span>
                    <span className="mt-0.5 block text-[11px] leading-snug text-content-secondary">
                      {type.objective}
                    </span>
                  </span>

                  {selected === type.id ? (
                    <i className="ri-check-line mt-0.5 shrink-0 text-sm text-primary-500" />
                  ) : null}
                </button>
              ))}
            </div>

            <Input
              label={t("monitoreoIa.units.callModal.factLabel")}
              hint={t("monitoreoIa.units.callModal.factHint")}
              value={fact}
              disabled={blocked}
              maxLength={280}
              onChange={(event) => setFact(event.target.value)}
              placeholder={t("monitoreoIa.units.callModal.factPlaceholder")}
            />

            <Textarea
              label={t("monitoreoIa.units.callModal.reasonLabel")}
              hint={t("monitoreoIa.units.callModal.reasonHint")}
              value={reason}
              disabled={blocked}
              maxLength={280}
              rows={2}
              onChange={(event) => setReason(event.target.value)}
              placeholder={t("monitoreoIa.units.callModal.reasonPlaceholder")}
            />

            {errorKey ? (
              <p className="rounded-xl border border-error/30 bg-error/10 px-3 py-2 text-xs text-error">
                {t(errorKey)}
              </p>
            ) : null}

            <Button
              fullWidth
              disabled={!selected || blocked || callUnit.isPending}
              loading={callUnit.isPending}
              onClick={placeCall}
              icon={
                callUnit.isPending ? undefined : <i className="ri-phone-line" />
              }
            >
              {callUnit.isPending
                ? t("monitoreoIa.units.callModal.calling")
                : selectedType
                  ? t("monitoreoIa.units.callModal.callType", {
                      type: selectedType.label,
                    })
                  : t("monitoreoIa.units.callModal.pickFirst")}
            </Button>
          </div>
        )
      }
    />
  );
}
